import { type FC, useCallback } from 'react';

import {
  DC_Domain_Models_Products_ProductDiscontinueReason,
  Products_FineCalculation_DiscontinueIntakeModel,
} from '@monorepo-types/dc';

import type { CancellationReasonContentName } from './cancellation-reason-step-content.types';
import {
  CancellationReasonStepBase,
  type CancellationReasonStepBaseProps,
  type CancellationReasonSubmitPayload,
} from './CancellationReasonStepBase';
import { createCancellationReasonMachine } from '../../machines/cancellationReasonMachine';
import { CancellationReasonFlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';

type CancellationReasonMachine = ReturnType<typeof createCancellationReasonMachine>;

interface Props {
  /** The name the flow's reason step is authored under, such as `DongleCancelationReasonStep`. */
  contentName: CancellationReasonContentName;
  reasonMachine: CancellationReasonMachine;
  /** When false, discontinue-disallow dialogs are ignored (dongle behaviour). Default true. */
  enableDisallowDialog?: boolean;
}

export const CancellationReasonStep: FC<Props> = ({ contentName, reasonMachine, enableDisallowDialog = true }) => {
  const { useFlowActorRef } = useFlowHooks<CancellationReasonFlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();

  const onSuccessfulSubmit = useCallback(
    ({ reason, reasonLabel, explanation, intake }: CancellationReasonSubmitPayload) => {
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          reason: reason === '' ? undefined : (reason as DC_Domain_Models_Products_ProductDiscontinueReason),
          reasonLabel,
          explanation,
          intake: intake as Products_FineCalculation_DiscontinueIntakeModel,
        },
      });
    },
    [sendToFlowMachine],
  );

  return (
    <CancellationReasonStepBase
      contentName={contentName}
      reasonMachine={reasonMachine as CancellationReasonStepBaseProps['reasonMachine']}
      unauthenticatedAction="okta"
      enableDeceasedReason
      disallowMode={enableDisallowDialog ? 'dialog' : 'ignore'}
      onSuccessfulSubmit={onSuccessfulSubmit}
    />
  );
};
